/**
 * Words several storefront pages say that belong to none of them, and the rule for this folder.
 *
 * `storefrontCopy/` is the storefront's default copy, a module per page (named for its folder
 * under apps/storefront/src/templates/pages/) plus `frame.ts`: the words a store shows where it has
 * written none, in its terms (`storefrontTerminology.ts`), name and currency. Plain strings and
 * in-store paths, never components, so the storefront's content hooks and the dashboard's theme
 * preview read the same text; they are also the defaults a store's own copy will be laid over
 * (docs/design-system/templates.md, "Tenant copy").
 *
 * Default copy is a store's own words: every heading, intro, label, link and button label, tab,
 * chip and sort label, placeholder, helper line, empty state, status label, confirmation and call
 * to action on a page. Platform words are the same on every store and never a store's to change:
 * errors and load-failure fallbacks, refusals and validation messages, write outcomes (toasts),
 * legal text and assents, compliance disclosures, the fairness readout, and the controls of the
 * opener and the battle claim. Platform words stay with the code that shows them or, when the
 * theme preview draws one too, go in an api-contract module named for their concern (the footer's
 * legal line is in `storefrontLegal.ts`), never in this folder.
 */
import { capitalizeTerm, type StorefrontTerminology } from "../storefrontTerminology.js";
import type { VolatilityLevel } from "../volatility.js";

/** A run of prose: plain text, or text linking to a page of the store. */
export type CopyRun = string | { text: string; href: string };

/** The headings over the shopper's best recent opens: Home's, and the catalog's. */
export const RECENT_OPENS_TITLES = { home: "Fire pulls", catalog: "Top hits" } as const;

/** A paged list's button, and the count line announced after each page loads. */
export const STOREFRONT_PAGING_COPY = {
  loadMore: "Load more",
  showing: (shown: number) => `Showing ${shown}`,
} as const;

/** A pull tile's button, which opens the pull's details, read with the item's name. */
export function pullDetailsLabel(itemName: string): string {
  return `Show ${itemName}`;
}

/** A player's name as a button to their profile: theirs, or `null` for the shopper's own. */
export function playerProfileLabel(name: string | null): string {
  return name === null ? "View your profile" : `View ${name}'s profile`;
}

/** A box tile's heart, read with the box's name. */
export function favoriteBoxLabel(boxName: string): string {
  return `Favorite ${boxName}`;
}

/** A volatility meter's words: its name, and each level's word, read in place of a number. */
export interface VolatilityWords {
  label: string;
  levels: Readonly<Record<VolatilityLevel, string>>;
}

/** The volatility meter on a box tile and the box page. */
export const VOLATILITY_WORDS: VolatilityWords = {
  label: "Volatility",
  levels: { low: "Low", medium: "Medium", high: "High", max: "Max" },
};

/** The link to the catalog, in the store's word for its boxes. */
export function browseBoxesLabel(terms: StorefrontTerminology): string {
  return `Browse ${terms.box.plural}`;
}

/** The link to Add funds beside a shortfall, in the currency's plural name. */
export function buyCurrencyLabel(currencyPlural: string): string {
  return `Buy ${currencyPlural}`;
}

/** The words of an item's details, opened from any card that shows an item. */
export interface ItemDetailsCopy {
  /** The window's name: "Card details". */
  title: string;
  value: string;
  chance: string;
  rarity: string;
  close: string;
}

/** An item's details in the store's word for an item. */
export function itemDetailsCopy(terms: StorefrontTerminology): ItemDetailsCopy {
  return {
    title: `${capitalizeTerm(terms.item.singular)} details`,
    value: "Value",
    chance: "Chance",
    rarity: "Rarity",
    close: "Close",
  };
}

/**
 * The boxes lists' words (the arcade layouts of the catalog and the Forge, which say them alike):
 * the switch between the store's list and the Forge's, the filters, the phone's sheet, and why a
 * list shows nothing. Price ranges take their amounts already formatted in the store's currency.
 */
export interface BoxListCopy {
  /** The switch's accessible name, and each list's name on it. */
  lists: { label: string; official: string; community: string };
  /** All or New, over the store's own boxes: the control's name and each choice. */
  tabs: { label: string; all: string; new: string };
  search: { label: string; placeholder: string; clear: string };
  price: {
    label: string;
    all: string;
    afford: string;
    under: (amount: string) => string;
    between: (from: string, to: string) => string;
    over: (amount: string) => string;
  };
  sort: {
    label: string;
    /** Opens, on the store's own list ("Most popular") and on the Forge's ("Most opened"). */
    popular: { official: string; community: string };
    new: string;
    old: string;
    priceDesc: string;
    priceAsc: string;
    favorites: string;
    /** The two kinds of choice in the list, read as its groups: an order, or a subset. */
    groups: { order: string; only: string };
  };
  volatility: string;
  /** A Forge card's count over its art, given the count already shortened ("3.4k"). */
  opens: (count: string) => string;
  /** The Forge's first tile: its info button's name and what it says. */
  create: { infoLabel: string; info: string };
  clear: string;
  sheet: {
    label: string;
    countLabel: (count: number) => string;
    title: string;
    clear: string;
    apply: string;
  };
  empty: {
    noMatches: { title: string; description: string; action: string };
    noFavorites: { title: string; description: string; action: string };
    /** The list has nothing in it at all. */
    none: string;
  };
}

export function defaultBoxListCopy(terms: StorefrontTerminology): BoxListCopy {
  const { box, forge } = terms;
  return {
    lists: {
      label: `${capitalizeTerm(box.singular)} lists`,
      official: "Official",
      community: capitalizeTerm(forge.singular),
    },
    tabs: { label: "Show", all: "All", new: "New" },
    search: { label: `Search ${box.plural}`, placeholder: "Search", clear: "Clear search" },
    price: {
      label: "Price range",
      all: "All prices",
      afford: "Within balance",
      under: (amount) => `Under ${amount}`,
      between: (from, to) => `${from} – ${to}`,
      over: (amount) => `${amount} and up`,
    },
    sort: {
      label: "Sort by",
      popular: { official: "Most popular", community: "Most opened" },
      new: "Newest",
      old: "Oldest",
      priceDesc: "Highest price",
      priceAsc: "Lowest price",
      favorites: "Favorites",
      groups: { order: "Order", only: "Show only" },
    },
    volatility: "Volatility",
    opens: (count) => `${count} opens`,
    create: {
      infoLabel: `About ${capitalizeTerm(forge.singular)}`,
      info: `Pick the ${terms.item.plural} that go in it, open it yourself, or share it and earn on every opening.`,
    },
    clear: "Clear filters",
    sheet: {
      label: "Filters",
      countLabel: (count) => `Filters, ${count} set`,
      title: "Filters",
      clear: "Clear",
      apply: "Apply",
    },
    empty: {
      noMatches: {
        title: `No ${box.plural} match`,
        description: "Try another name, price or volatility.",
        action: "Clear filters",
      },
      noFavorites: {
        title: "No favorites yet",
        description: `Heart any ${box.singular} to keep it here.`,
        action: `Show every ${box.singular}`,
      },
      none: `No ${box.plural} yet.`,
    },
  };
}
